import { roundWinnerIds, type GameState, type MatchState, type RoundSummary } from "../types/game";

export interface LeaderboardEntry {
  playerId: string;
  nickname: string;
  /** Position points for the round: 1 = went out first. Fewest wins. */
  points: number;
  /** Cards still in hand when the round ended, shown alongside the points. */
  cardsLeft: number;
}

/**
 * Position scoring (design doc "Winning & Scoring"): a player's points are
 * "how many players finished with strictly fewer cards" + 1.
 *
 *   - The player who went out has 0 cards, so nobody is below them: 1 point.
 *   - Ties share a value and the NEXT player absorbs the gap. With one player
 *     out and two players holding 1 card, the next player holding 2 cards has
 *     three players below them, so they score 4.
 *
 * This replaces the old "1 point per card left", which let a single blowout
 * round dominate a whole match. `cardsLeft` is reported separately so the
 * leaderboard can still tell the story of how close the round was.
 */
export function computeRoundScores(state: GameState): {
  scores: Record<string, number>;
  cardsLeft: Record<string, number>;
} {
  const cardsLeft: Record<string, number> = {};
  for (const p of state.players) cardsLeft[p.id] = p.deck.length;
  return { scores: scoresFromCardCounts(cardsLeft), cardsLeft };
}

/**
 * Position scoring over an arbitrary set of players: the same "how many finished
 * with strictly fewer cards, plus one" as `computeRoundScores`, taken off the
 * counts alone.
 *
 * Split out because a party ranks its players across every table at once. The
 * field a player finished against is the whole party, not the four or five
 * people they happened to be dealt in with, so the parent re-scores the union of
 * every table's counts through here rather than gluing the per-table results
 * together. Mirrors `ScoresFromCardCounts` in the Go engine, which is the copy
 * that actually runs it (docs/PARTY_MODE.md).
 */
export function scoresFromCardCounts(cardsLeft: Record<string, number>): Record<string, number> {
  const counts = Object.values(cardsLeft);
  const scores: Record<string, number> = {};
  for (const [id, left] of Object.entries(cardsLeft)) {
    scores[id] = counts.filter((other) => other < left).length + 1;
  }
  return scores;
}

/** Sorted ascending: fewest points (the round winner, 1) first. */
export function buildLeaderboard(state: GameState): LeaderboardEntry[] {
  if (!state.scores) return [];
  return state.players
    .map((p) => ({
      playerId: p.id,
      nickname: p.nickname,
      points: state.scores![p.id] ?? p.deck.length,
      cardsLeft: state.cardsLeft?.[p.id] ?? p.deck.length,
    }))
    .sort((a, b) => a.points - b.points || a.cardsLeft - b.cardsLeft);
}

export interface MatchLeaderboardEntry {
  playerId: string;
  nickname: string;
  /** Sum of position points across every completed round. Fewest wins. */
  total: number;
  roundsWon: number;
  roundsPlayed: number;
}

/**
 * Running totals for the match so far. A match never formally ends — this is
 * available after any round, however many have been played.
 */
export function buildMatchLeaderboard(match: MatchState, nicknames: Record<string, string>): MatchLeaderboardEntry[] {
  const ids = new Set<string>(Object.keys(match.totals));
  for (const r of match.rounds) for (const id of Object.keys(r.scores)) ids.add(id);

  return [...ids]
    .map((playerId) => ({
      playerId,
      nickname: nicknames[playerId] ?? playerId,
      total: match.totals[playerId] ?? 0,
      // Over the winner LIST, not the single id: a party's round finishes once
      // per table and every one of those is a win. Reading `winnerPlayerId`
      // alone credited only the first table's, so a player who went out at any
      // other table showed nought wins beside a perfectly correct total.
      roundsWon: match.rounds.filter((r) => roundWinnerIds(r).includes(playerId)).length,
      roundsPlayed: match.rounds.filter((r) => r.scores[playerId] !== undefined).length,
    }))
    .sort((a, b) => a.total - b.total || b.roundsWon - a.roundsWon);
}

/**
 * Bounds the per-round history kept in a match. Totals are accumulated
 * separately, so trimming the history never changes anyone's score — it only
 * limits how far back the round-by-round breakdown goes. A match never formally
 * ends, and this record is persisted (localStorage locally, a Firestore document
 * online), so it needs a ceiling.
 */
export const MAX_STORED_ROUNDS = 100;

/**
 * Carries a match across a change to who is sitting at the table.
 *
 * Called when the host re-sets the table between rounds and chooses to KEEP the
 * leaderboard. Two things happen, and both are about the board still telling the
 * truth afterwards.
 *
 * **A new seat starts level with last place, not on nothing.** Fewest points
 * wins, so a seat left on zero would arrive clear at the TOP of a match it has
 * not played. Matching last place is the position that claims nothing — no worse
 * off than anyone at the table, and no better. It is the same rule the online
 * deal applies to players promoted off the waiting list (`seedJoinerTotals` in
 * the Go manager), written again here because local play has no server to ask
 * and the unfairness is identical.
 *
 * **A seat that has gone comes off the board entirely**, history included.
 * Keeping their total was the obvious alternative and it is worse in both
 * directions: `buildMatchLeaderboard` draws its player list from the round
 * history as well as the totals, so a departed player cannot be removed by
 * dropping their total alone — that just shows them on zero, at the top — and
 * left in, they are drawn under their raw player id, because the nicknames come
 * from the seats currently at the table. Their name in `match.nicknames` goes
 * with their scores, so the record stays names-for-exactly-the-board. Nobody else's number moves either way:
 * totals are accumulated per player, so removing one player's scores from the
 * history changes only what is counted about them.
 *
 * Deliberately NOT part of `recordRound`, which folds in a round everybody
 * played and has no opinion about who is about to sit down. Pure. A match with
 * nothing banked is returned untouched — everyone is on zero there, and an
 * absent total and an explicit zero are the same thing.
 */
export function reseatMatch(match: MatchState, seatIds: readonly string[]): MatchState {
  if (match.rounds.length === 0) return match;
  const seated = new Set(seatIds);
  const lastPlace = Math.max(0, ...seatIds.map((id) => match.totals[id] ?? 0));

  const totals: Record<string, number> = {};
  for (const id of seatIds) totals[id] = match.totals[id] ?? lastPlace;

  const rounds = match.rounds.map((r) => ({
    ...r,
    scores: pickSeated(r.scores, seated),
    cardsLeft: pickSeated(r.cardsLeft, seated),
    winnerPlayerId: r.winnerPlayerId && seated.has(r.winnerPlayerId) ? r.winnerPlayerId : null,
    winnerPlayerIds: roundWinnerIds(r).filter((id) => seated.has(id)),
  }));

  // A departed seat's name goes with its scores, so the record stays one
  // consistent thing: names for exactly the ids on the board. (Online keeps
  // both - that board has no reseat, and its departed players stay on it.)
  if (!match.nicknames) return { ...match, totals, rounds };
  const nicknames: Record<string, string> = {};
  for (const [id, name] of Object.entries(match.nicknames)) if (seated.has(id)) nicknames[id] = name;
  return { ...match, totals, rounds, nicknames };
}

function pickSeated(byPlayer: Record<string, number>, seated: ReadonlySet<string>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, value] of Object.entries(byPlayer)) if (seated.has(id)) out[id] = value;
  return out;
}

/**
 * Last place's score in a round, for each member who sat it out and is already
 * on the board. Mirrors satOut in the Go engine (observers.go): a member with no
 * total has never played and is seeded at the deal instead.
 */
function satOutPoints(
  scores: Record<string, number>,
  totals: Record<string, number>,
  sittingOut: readonly string[],
): Record<string, number> | undefined {
  const points = Object.values(scores);
  if (points.length === 0) return undefined;
  const last = Math.max(...points);
  let out: Record<string, number> | undefined;
  for (const id of sittingOut) {
    if (id in scores || !(id in totals)) continue;
    (out ??= {})[id] = last;
  }
  return out;
}

/**
 * Folds a finished round into the match record and advances the round number.
 * Pure: returns a new MatchState. Ties are left as ties — no tiebreak is applied.
 *
 * `sittingOut` is online only: the members of the lobby who were not dealt this
 * round (watching, or waiting for the deal). Any of them already on the board
 * is given last place's score for it (`satOut`), as the Go server's RecordRound
 * does - a local table has nobody sitting out.
 */
export function recordRound(match: MatchState, state: GameState, sittingOut: readonly string[] = []): MatchState {
  if (!state.scores || !state.cardsLeft) return match;
  // Idempotent: a round is only ever folded in once, even if the caller retries.
  if (match.rounds.some((r) => r.roundNumber === state.roundNumber)) return match;

  const summary: RoundSummary = {
    roundNumber: state.roundNumber,
    scores: { ...state.scores },
    cardsLeft: { ...state.cardsLeft },
    winnerPlayerId: state.winnerPlayerId,
    // A party's parent carries one winner per table; an ordinary table has the
    // single winner and says so the same way.
    winnerPlayerIds: state.roundWinnerIds?.length
      ? [...state.roundWinnerIds]
      : state.winnerPlayerId
        ? [state.winnerPlayerId]
        : [],
    finishedAtMs: Date.now(),
  };
  const totals = { ...match.totals };
  for (const [playerId, points] of Object.entries(summary.scores)) {
    totals[playerId] = (totals[playerId] ?? 0) + points;
  }
  const satOut = satOutPoints(summary.scores, totals, sittingOut);
  if (satOut) {
    summary.satOut = satOut;
    for (const [playerId, points] of Object.entries(satOut)) totals[playerId] += points;
  }
  // Everyone at the table, latest name wins. The match carries them so the
  // board can still name somebody who has left the roster since (online, the
  // server also names them on the way out).
  const nicknames = { ...match.nicknames };
  for (const p of state.players) if (p.nickname) nicknames[p.id] = p.nickname;
  return {
    ...match,
    roundNumber: state.roundNumber + 1,
    totals,
    rounds: [...match.rounds, summary].slice(-MAX_STORED_ROUNDS),
    nicknames,
  };
}
